/**
 * The display name of a dispensary, city or centre, in the reader's own
 * language.
 *
 * ## Why this became necessary, and why it was invisible before
 *
 * Every one of these rows carries `name_en` and `name_ar`. A dozen call
 * sites read `name_en` UNCONDITIONALLY — `label: c.name_en` in the case
 * form's city picker, `record.dispensary?.name_en` on the case detail — and
 * nobody noticed, because in the seeded reference data `name_en` was not
 * English. `cities.name_en` held the Arabic city name, and
 * `dispensaries.name_ar` held the Latin one. An Arabic operator reading
 * `name_en` got Arabic by accident, and the bug was invisible for exactly
 * as long as the data was wrong.
 *
 * `translations.php` fixed the data. `name_en` is now genuinely English, so
 * every unconditional read of it became a visible defect in the same commit
 * — an Arabic session showing "Khamis Mushait" where it used to show
 * "خميس مشيط". This function is what those call sites use instead.
 *
 * It is the SAME rule `deviceFormHelpers.ts`'s `deviceModelLabel()` already
 * applies to `DeviceModel`, and `SensorList.tsx`'s `typeLabel()` to a
 * device's own `type_label_en`/`type_label_ar`. A separate function rather
 * than a widened `deviceModelLabel`: that one takes a `DeviceModel`, and
 * widening its parameter to "anything with two name columns" would let a
 * genuine mistake — passing a case, a user — type-check.
 *
 * `startsWith('ar')`, not `=== 'ar'`, matching every existing locale check
 * in this codebase: i18next resolves to `ar-SA` in some browsers.
 */
export interface LocalisedName {
  readonly name_en: string;
  readonly name_ar: string;
}

export function referenceName(node: LocalisedName, language: string): string {
  return language.startsWith('ar') ? node.name_ar : node.name_en;
}

/**
 * The same, for a relation the API may answer as `null` — `case.dispensary`
 * and `case.centre` are both optional. Answers `null` so the caller keeps
 * its own "not recorded" copy rather than this module inventing one.
 */
export function optionalReferenceName(
  node: LocalisedName | null | undefined,
  language: string,
): string | null {
  return node === null || node === undefined ? null : referenceName(node, language);
}
